import { getRegionLocation, regionSearchText, normalizeLocationSearch, formatRegionLocation } from '../utils/regionLocation';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip,
  IconButton,
  Popover,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import api, { BotStatusResponse, CreateBotRegionSelection, Region, normalizeBotRegions } from '../services/api';
import { Scenario, ScenarioS3ConfigPayload } from '../types/attacks';
import { OperationsView, AttackStatus } from '../types/operations';
import { useNotification } from '../contexts/NotificationContext';
import { useTenant } from '../contexts/TenantContext';
import { PermissionGate } from '../components/PermissionGate';
import ScenarioSelectionView from '../components/operations/ScenarioSelectionView';
import AttackControlView from '../components/operations/AttackControlView';
import CreateScenarioDialog from '../components/operations/CreateScenarioDialog';
import ScenarioDebugDialog from '../components/operations/ScenarioDebugDialog';
import EndScenarioImpactBotsDialog from '../components/operations/EndScenarioImpactBotsDialog';
import { SCENARIO_CHANGED_EVENT } from '../constants/scenarioEvents';
import { normalizeConfigurationForForm } from '../utils/attackConfig';
import { providerLabel, providerChipColor } from '../utils/hostingProvider';
import { cloneScenarioConfiguration, generateDuplicateScenarioName } from '../utils/scenarioDuplication';

const getAttackStatusFromCommand = (command: any): AttackStatus | null => {
  const normalized = String(command || '').toLowerCase();
  if (normalized === 'run') return 'running';
  if (normalized === 'pause') return 'paused';
  if (normalized === 'shutdown') return 'stopped';
  return null;
};

type PendingBotTarget = {
  target: number;
  direction: 'up' | 'down';
  startedAt: number;
};

const BOT_TARGET_TIMEOUT_MS = 60_000;

const Operations: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showNotification, registerClearAllListener } = useNotification();
  const { selectedTenant } = useTenant();
  
  const getAttackClassColor = (attackClass?: string) => {
    switch (attackClass?.toLowerCase()) {
      case 'application':
        return '#00f0ff'; // Cyan
      case 'infrastructure':
        return '#ff006e'; // Pink
      case 'volumetric':
        return '#ffc107'; // Yellow
      default:
        return '#00b4d8'; // Blue
    }
  };

  // View state
  const [currentView, setCurrentView] = useState<OperationsView>('scenario-selection');
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Scenario state
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [currentScenario, setCurrentScenario] = useState<Scenario | null>(null);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [scenariosError, setScenariosError] = useState<string | null>(null);
  /** scenarioId -> true if all targets in allow list; false if not; undefined while loading */
  const [scenarioAllowListOk, setScenarioAllowListOk] = useState<Record<string, boolean>>({});
  /** scenarioId -> list of target strings that are not in the allow list */
  const [scenarioInvalidTargets, setScenarioInvalidTargets] = useState<Record<string, string[]>>({});
  
  // Attack control state
  const [botCount, setBotCount] = useState(10);
  const [threadCount, setThreadCount] = useState(5);
  const [attackStatus, setAttackStatus] = useState<AttackStatus>('stopped');
  const awaitingUserStartRef = useRef(false);
  const isStartAttackInProgressRef = useRef(false);
  const [attackConfig, setAttackConfig] = useState<Record<string, any>>({});
  const [detailedBotStatus, setDetailedBotStatus] = useState<BotStatusResponse | null>(null);
  const [botStatus, setBotStatus] = useState<{
    pending: number;
    running: number;
    shuttingDown: number;
    total: number;
  }>({ pending: 0, running: 0, shuttingDown: 0, total: 0 });
  const [pendingBotTarget, setPendingBotTarget] = useState<PendingBotTarget | null>(null);
  
  // Regions state
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [scenarioHosting, setScenarioHosting] = useState<string | null>(null);

  useEffect(() => {
    if (!currentScenario) { setScenarioHosting(null); return; }
    const target = (currentScenario.configuration as any)?.targets?.[0];
    if (!target) { setScenarioHosting(null); return; }
    api.getAssets(target).then((assets) => {
      const match = assets.find((a) => a.discovery?.hosting_provider);
      setScenarioHosting(match?.discovery?.hosting_provider ?? null);
    }).catch(() => setScenarioHosting(null));
  }, [currentScenario]);

  const regionsFilterSummary = React.useMemo(() => {
    const STORAGE_KEY = 'operations.regionsFiltersState';

    type StoredFilter = { id?: string; query?: string; label?: string };
    type StoredState = {
      scenarioId?: string | null;
      activeFilters?: StoredFilter[];
      runningOnly?: boolean;
      providerTypeFilters?: string[];
    };

    const safeParse = (): StoredState | null => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as StoredState;
      } catch {
        return null;
      }
    };

    const stored = safeParse();
    const runningOnly = Boolean(stored?.runningOnly);
    const providerTypeFilters = Array.isArray(stored?.providerTypeFilters)
      ? stored!.providerTypeFilters!.map((v) => String(v)).filter(Boolean)
      : [];
    const activeFilters = Array.isArray(stored?.activeFilters)
      ? stored!.activeFilters!
          .map((f) => ({
            id: String(f?.id ?? ''),
            query: String(f?.query ?? '').trim(),
            label: String(f?.label ?? f?.query ?? '').trim(),
          }))
          .filter((f) => Boolean(f.query))
      : [];

    type ParsedFilters = {
      continent?: string;
      providerType?: string;
      providerName?: string;
      regionName?: string;
      regionCode?: string;
      location?: string;
      status?: string;
      configured?: string;
      bots?: string;
      capacity?: string;
      freeText?: string;
      negations: Set<string>;
    };

    const parseFilterQuery = (queryStr: string): ParsedFilters | null => {
      const trimmed = queryStr.trim().toLowerCase();
      if (!trimmed) return null;
      const filters: ParsedFilters = { negations: new Set() };

      const filterPattern = /(!?)(\w+)(?:=|:)\s*"([^"]*)"|(!?)(\w+)(?:=|:)\s*([^\s]+)/gi;
      let match: RegExpExecArray | null;
      let lastIndex = 0;
      const freeTextParts: string[] = [];

      while ((match = filterPattern.exec(trimmed)) !== null) {
        if (match.index > lastIndex) {
          freeTextParts.push(trimmed.substring(lastIndex, match.index).trim());
        }
        lastIndex = match.index + match[0].length;

        const negation = match[1] || match[4];
        const key = (match[2] || match[5]).toLowerCase();
        let value = (match[3] !== undefined ? match[3] : match[6]).toLowerCase();
        value = value.replace(/\*/g, '');

        if (key === 'continent') {
          filters.continent = value;
          if (negation) filters.negations.add('continent');
        } else if (key === 'provider_type' || key === 'type') {
          filters.providerType = value;
          if (negation) filters.negations.add('providerType');
        } else if (key === 'provider' || key === 'provider_name') {
          filters.providerName = value;
          if (negation) filters.negations.add('providerName');
        } else if (key === 'region' || key === 'region_name' || key === 'name') {
          filters.regionName = value;
          if (negation) filters.negations.add('regionName');
        } else if (key === 'region_code' || key === 'code') {
          filters.regionCode = value;
          if (negation) filters.negations.add('regionCode');
        } else if (key === 'location') {
          filters.location = value;
          if (negation) filters.negations.add('location');
        } else if (key === 'status') {
          filters.status = value;
          if (negation) filters.negations.add('status');
        } else if (key === 'configured') {
          filters.configured = value;
          if (negation) filters.negations.add('configured');
        } else if (key === 'bots' || key === 'machine_count') {
          filters.bots = value;
          if (negation) filters.negations.add('bots');
        } else if (key === 'capacity') {
          filters.capacity = value;
          if (negation) filters.negations.add('capacity');
        }
      }

      if (lastIndex < trimmed.length) {
        freeTextParts.push(trimmed.substring(lastIndex).trim());
      }
      const freeText = freeTextParts.filter(Boolean).join(' ').trim();
      if (freeText) filters.freeText = freeText;

      return filters;
    };

    const matchesNumericFilter = (rawFilter: string | undefined, actualValue: number): boolean => {
      if (!rawFilter) return true;
      const normalized = rawFilter.replace(/\s/g, '');
      const match = normalized.match(/^(>=|<=|>|<|=)?(-?\d+(?:\.\d+)?)$/);
      if (!match) return false;
      const operator = match[1] || '=';
      const target = Number(match[2]);
      switch (operator) {
        case '>':
          return actualValue > target;
        case '>=':
          return actualValue >= target;
        case '<':
          return actualValue < target;
        case '<=':
          return actualValue <= target;
        default:
          return actualValue === target;
      }
    };

    const matchesStringFilter = (value: string, rawFilter: string, isNegated: boolean) => {
      value = normalizeLocationSearch(value);
      rawFilter = normalizeLocationSearch(rawFilter);
      const terms = rawFilter
        .split(/[,&|]/)
        .map((term) => term.trim())
        .filter(Boolean);
      if (terms.length === 0) return true;
      const matchesAny = terms.some((term) => value.includes(term));
      return isNegated ? !matchesAny : matchesAny;
    };

    const matchesParsedFilters = (region: Region, filters: ParsedFilters) => {
      const provider = region.provider;

      if (filters.continent) {
        const isNegated = filters.negations.has('continent');
        if (!matchesStringFilter(getRegionLocation(region).continent.toLowerCase(), filters.continent, isNegated)) return false;
      }
      if (filters.providerType) {
        if (!provider) return false;
        const isNegated = filters.negations.has('providerType');
        if (!matchesStringFilter((provider.provider_type || '').toLowerCase(), filters.providerType, isNegated)) return false;
      }
      if (filters.providerName) {
        if (!provider) return false;
        const isNegated = filters.negations.has('providerName');
        if (!matchesStringFilter((provider.name || '').toLowerCase(), filters.providerName, isNegated)) return false;
      }
      if (filters.regionName) {
        const isNegated = filters.negations.has('regionName');
        if (!matchesStringFilter(regionSearchText(region), filters.regionName, isNegated)) return false;
      }
      if (filters.regionCode) {
        const isNegated = filters.negations.has('regionCode');
        if (!matchesStringFilter((region.region_code || '').toLowerCase(), filters.regionCode, isNegated)) return false;
      }
      if (filters.location) {
        const isNegated = filters.negations.has('location');
        if (!matchesStringFilter(normalizeLocationSearch(getRegionLocation(region).label), filters.location, isNegated)) return false;
      }
      if (filters.status) {
        const isNegated = filters.negations.has('status');
        if (!matchesStringFilter((region.status || '').toLowerCase(), filters.status, isNegated)) return false;
      }
      if (filters.configured) {
        const configuredValue = ['true', '1', 'yes', 'y'].includes(filters.configured);
        const matches = region.configured === configuredValue;
        const isNegated = filters.negations.has('configured');
        if (isNegated ? matches : !matches) return false;
      }
      if (filters.bots) {
        const matches = matchesNumericFilter(filters.bots, region.machine_count || 0);
        const isNegated = filters.negations.has('bots');
        if (isNegated ? matches : !matches) return false;
      }
      if (filters.capacity) {
        const capacityValue = (region.capacity ?? (region as any).capacity ?? 0) as number;
        const matches = matchesNumericFilter(filters.capacity, capacityValue);
        const isNegated = filters.negations.has('capacity');
        if (isNegated ? matches : !matches) return false;
      }

      if (filters.freeText) {
        const searchText = `${regionSearchText(region)} ${provider?.name || ''} ${provider?.provider_type || ''}`.toLowerCase();
        return matchesStringFilter(searchText, filters.freeText, false);
      }

      return true;
    };

    const passesQuickFilters = (region: Region) => {
      if (runningOnly && (region.machine_count || 0) <= 0) return false;
      if (providerTypeFilters.length > 0) {
        const providerType = region.provider?.provider_type?.toLowerCase();
        const allowed = providerTypeFilters.map((v) => v.toLowerCase());
        if (!providerType || !allowed.includes(providerType)) return false;
      }
      return true;
    };

    const runningCount = regions.filter((region) => {
      if ((region.machine_count || 0) <= 0) return false;
      if (providerTypeFilters.length === 0) return true;
      const providerType = region.provider?.provider_type?.toLowerCase();
      const allowed = providerTypeFilters.map((v) => v.toLowerCase());
      return Boolean(providerType) && allowed.includes(providerType as string);
    }).length;

    const providerTypeCounts = providerTypeFilters.reduce<Record<string, number>>((acc, type) => {
      const key = type.toLowerCase();
      acc[key] = regions.filter((region) => {
        if (runningOnly && (region.machine_count || 0) <= 0) return false;
        return (region.provider?.provider_type || '').toLowerCase() === key;
      }).length;
      return acc;
    }, {});

    const activeFilterCounts = activeFilters.reduce<Record<string, number>>((acc, filter) => {
      const key = filter.id || filter.query;
      const parsed = parseFilterQuery(filter.query);
      if (!parsed) {
        acc[key] = regions.filter(passesQuickFilters).length;
        return acc;
      }
      acc[key] = regions.filter((region) => passesQuickFilters(region) && matchesParsedFilters(region, parsed)).length;
      return acc;
    }, {});

    // Compute in-scope count directly from stored filters so it is correct on
    // initial load, before RegionsPanel has mounted and fired its callback.
    const parsedActiveFilterQueries = activeFilters
      .map((f) => parseFilterQuery(f.query))
      .filter((p): p is NonNullable<typeof p> => Boolean(p));

    const computedInScopeCount = regions.filter((region) => {
      if (!passesQuickFilters(region)) return false;
      if (parsedActiveFilterQueries.length === 0) return true;
      return parsedActiveFilterQueries.some((parsed) => matchesParsedFilters(region, parsed));
    }).length;

    return {
      runningOnly,
      runningCount,
      providerTypeFilters,
      providerTypeCounts,
      activeFilters,
      activeFilterCounts,
      // Prefer explicit manual selection from RegionsPanel once available;
      // otherwise use the count derived from stored filters.
      inScopeCount: selectedRegions.length > 0 ? selectedRegions.length : computedInScopeCount,
      totalRegions: regions.length,
    };
  }, [regions, selectedRegions]);
  // Unique key is provider_id + region_id (same region_id can exist for different providers)
  const regionRunningCounts = React.useMemo(() => {
    const map: Record<string, number> = {};
    const normalizedRegions = normalizeBotRegions(detailedBotStatus?.regions);
    const pid = (id: string | null | undefined) => (id ?? '').toLowerCase();
    normalizedRegions.forEach((region) => {
      const running = region.running_count ?? 0;
      const pending = region.pending_count ?? 0;
      const active = running + pending;
      const providerId = pid(region.provider_id);
      const keys = [region.region_id, region.region_name]
        .filter((value): value is string => Boolean(value))
        .map((value) => `${providerId}:${value.toLowerCase()}`);
      keys.forEach((key) => {
        map[key] = active;
      });
    });
    return map;
  }, [detailedBotStatus]);
  // Total capacity per provider+region shown as the capacity denominator in the
  // regions table. The backend computes the provider-specific meaning in `capacity_total`:
  //   - Azure/GCP: active warm-pool reservations. "Active" = reservation units in
  //     a usable state (free + claimed + assigned); releasing/failed are excluded.
  //   - AWS/OpenStack: the region's full quota-based instance capacity (effective limit),
  //     since they do not use the warm-pool reservation model.
  // Sourced from /regions (readable by every authenticated user) rather than the
  // global-admin-only /providers reservation and capacity endpoints, so operators and
  // tenant admins see the same denominator instead of "/ 0".
  const regionCapacityCounts = React.useMemo(() => {
    const map: Record<string, number> = {};
    const pid = (id: string | null | undefined) => (id ?? '').toLowerCase();
    regions.forEach((region) => {
      if (region.capacity_total === null || region.capacity_total === undefined) return;
      const total = Math.max(0, Math.round(region.capacity_total));
      const provider = pid(region.provider?.id);
      // Key on every region identifier the panel may look up (region_code, id, name)
      // so it resolves regardless of which one the region row carries.
      [region.region_code, region.id, region.name]
        .filter((value): value is string => Boolean(value))
        .forEach((value) => {
          map[`${provider}:${value.toLowerCase()}`] = total;
        });
    });
    return map;
  }, [regions]);
  const displayBotStatus = React.useMemo(() => {
    if (!pendingBotTarget) return botStatus;
    const actualTotal = botStatus.running + botStatus.pending;
    const delta = Math.max(0, Math.abs(pendingBotTarget.target - actualTotal));
    if (delta === 0) return botStatus;
    return {
      ...botStatus,
      pending: Math.max(botStatus.pending, delta),
    };
  }, [botStatus, pendingBotTarget]);

  /** Normalized attack targets for the active scenario (from attackConfig or scenario.configuration). */
  const attackTargets = useMemo(() => {
    const fromConfig = attackConfig?.targets;
    const fromScenario = (currentScenario?.configuration as any)?.targets;
    const raw = Array.isArray(fromConfig) && fromConfig.length > 0
      ? fromConfig
      : Array.isArray(fromScenario)
        ? fromScenario
        : [];
    return raw
      .filter(Boolean)
      .map((t) => String(t).trim())
      .filter(Boolean);
  }, [currentScenario, attackConfig]);

  /** Anchor for "Expand" popover when there are many targets. */
  const [targetsExpandAnchor, setTargetsExpandAnchor] = useState<HTMLElement | null>(null);

  const handleCopyTargets = useCallback(() => {
    const text = attackTargets.join(', ');
    navigator.clipboard.writeText(text).then(
      () => showNotification('Targets copied to clipboard', 'success'),
      () => showNotification('Failed to copy targets', 'error')
    );
  }, [attackTargets, showNotification]);

  // Track if control panel has unsaved changes
  const [controlPanelDirty, setControlPanelDirty] = useState(false);
  
  // Intersection Observer for sticky navbar
  const controlRef = useRef<HTMLDivElement>(null);
  
  // Create scenario dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editScenario, setEditScenario] = useState<Scenario | null>(null);
  const handledAssetPrefillRef = useRef<string>('');

  // Delete scenario dialog
  const [deleteConfirmScenario, setDeleteConfirmScenario] = useState<Scenario | null>(null);
  const [debugScenario, setDebugScenario] = useState<Scenario | null>(null);
  const [deletingScenario, setDeletingScenario] = useState(false);

  // Track if scenarios are currently being loaded to prevent duplicate calls
  const loadingScenariosRef = useRef(false);

  // Setting scenario loading overlay
  const [settingScenario, setSettingScenario] = useState(false);
  const [endingScenario, setEndingScenario] = useState(false);

  // Impact bots prompt shown before an End Scenario actually ends the scenario
  const [endScenarioDialogOpen, setEndScenarioDialogOpen] = useState(false);
  const [settingCounts, setSettingCounts] = useState(false);
  const [regionActionLoading, setRegionActionLoading] = useState(false);
  const useSSE = process.env.REACT_APP_ENABLE_SSE !== 'false';

  const updateAwaitingUserStart = useCallback((next: boolean) => {
    awaitingUserStartRef.current = next;
  }, []);

  const getAttackStartFlagKey = useCallback((scenarioId: string, tenantId?: string | null) => {
    return `operations.attackStarted.${tenantId || 'unknown'}.${scenarioId}`;
  }, []);

  const readAttackStartFlag = useCallback(
    (scenarioId: string, tenantId?: string | null) => {
      try {
        return localStorage.getItem(getAttackStartFlagKey(scenarioId, tenantId)) === '1';
      } catch {
        return false;
      }
    },
    [getAttackStartFlagKey]
  );

  const writeAttackStartFlag = useCallback(
    (scenarioId: string, tenantId: string | null | undefined, value: boolean) => {
      try {
        const key = getAttackStartFlagKey(scenarioId, tenantId);
        if (value) {
          localStorage.setItem(key, '1');
        } else {
          localStorage.removeItem(key);
        }
      } catch {
        // ignore storage errors
      }
    },
    [getAttackStartFlagKey]
  );

  const toErrorText = useCallback((value: any): string => {
    if (value == null) return 'Unknown error';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.map(toErrorText).join('\n');
    if (typeof value === 'object') {
      const anyVal: any = value;
      if (typeof anyVal.message === 'string') return anyVal.message;
      if (typeof anyVal.msg === 'string') return anyVal.msg;
      if (typeof anyVal.detail === 'string') return anyVal.detail;
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }, []);

  const normalizeConfigForUi = useCallback(
    (config: Record<string, any> | undefined, attackTypeId?: string) =>
      normalizeConfigurationForForm(config, attackTypeId),
    []
  );


  const applyLiveBotStatus = useCallback(
    (pendingCount: number, runningCount: number, shuttingDownCount: number) => {
      const total = pendingCount + runningCount;
      setBotStatus({
        pending: pendingCount,
        running: runningCount,
        shuttingDown: shuttingDownCount,
        total,
      });

      if (pendingBotTarget) {
        const targetReached =
          pendingBotTarget.direction === 'up'
            ? total >= pendingBotTarget.target
            : total <= pendingBotTarget.target;
        const targetExpired = Date.now() - pendingBotTarget.startedAt > BOT_TARGET_TIMEOUT_MS;

        if (targetReached || targetExpired) {
          setPendingBotTarget(null);
          setBotCount(total);
        } else {
          setBotCount(pendingBotTarget.target);
        }
      } else {
        setBotCount(total);
      }
    },
    [pendingBotTarget]
  );

  const refreshBotInfo = useCallback(async () => {
    try {
      const detailed = await api.getDetailedBotStatus();
      setDetailedBotStatus(detailed);
      const pending = detailed.total_pending || 0;
      const running = detailed.total_running || 0;
      const shuttingDown = detailed.total_shutting_down || 0;
      applyLiveBotStatus(pending, running, shuttingDown);
    } catch (e) {
      console.error('Error refreshing bot info:', e);
    }
  }, [applyLiveBotStatus]);

  const loadAttackConfig = useCallback(async (
    scenario: Scenario,
    options?: { skipStatusUpdate?: boolean; s3_config?: ScenarioS3ConfigPayload }
  ) => {
    const skipStatusUpdate = options?.skipStatusUpdate ?? false;
    const shouldSkipStatusUpdate = skipStatusUpdate || awaitingUserStartRef.current || isStartAttackInProgressRef.current;
    try {
      const s3Config = options?.s3_config ?? await api.getScenarioS3Config(scenario.id);
      if (s3Config.exists && s3Config.s3_config) {
        const fullConfig = s3Config.s3_config.full_config || s3Config.s3_config;
        const normalizedConfig = normalizeConfigForUi(fullConfig, scenario.attack_type?.id);
        const nextThreads =
          typeof normalizedConfig.threads === 'number'
            ? normalizedConfig.threads
            : s3Config.s3_config.threads || 5;
        // Set threads from S3-derived config
        setThreadCount(nextThreads);
        // Set attack config from S3 in UI shape
        setAttackConfig(normalizedConfig);
        const command =
          (fullConfig as any)?.command ??
          (s3Config.s3_config as any).command;
        const statusFromCommand = getAttackStatusFromCommand(command);
        if (statusFromCommand && !shouldSkipStatusUpdate) {
          setAttackStatus(statusFromCommand);
        }
      } else {
        // Fallback to scenario.configuration
        if (scenario.configuration) {
          const config = typeof scenario.configuration === 'string'
            ? JSON.parse(scenario.configuration)
            : scenario.configuration;
          const normalizedConfig = normalizeConfigForUi(config, scenario.attack_type?.id);
          setThreadCount(normalizedConfig.threads || 5);
          setAttackConfig(normalizedConfig);
          const statusFromCommand = getAttackStatusFromCommand(config.command);
          if (statusFromCommand && !shouldSkipStatusUpdate) {
            setAttackStatus(statusFromCommand);
          }
        }
      }
    } catch (error) {
      console.error('Error loading S3 config:', error);
      // Fallback to scenario.configuration
      if (scenario.configuration) {
        try {
          const config = typeof scenario.configuration === 'string'
            ? JSON.parse(scenario.configuration)
            : scenario.configuration;
          const normalizedConfig = normalizeConfigForUi(config, scenario.attack_type?.id);
          setThreadCount(normalizedConfig.threads || 5);
          setAttackConfig(normalizedConfig);
          const statusFromCommand = getAttackStatusFromCommand(config.command);
          if (statusFromCommand && !shouldSkipStatusUpdate) {
            setAttackStatus(statusFromCommand);
          }
        } catch (e) {
          console.error('Error parsing scenario configuration:', e);
        }
      }
    }
  }, [normalizeConfigForUi]);
  
  // Error dialog
  const [errorDialog, setErrorDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    details?: string[];
  }>({
    open: false,
    title: '',
    message: '',
    details: []
  });

  // When user clicks "Clear All" in the notification panel, also close the error dialog
  useEffect(() => {
    return registerClearAllListener(() => {
      setErrorDialog((prev) => (prev.open ? { ...prev, open: false } : prev));
    });
  }, [registerClearAllListener]);

  // Check for active scenario on mount
  useEffect(() => {
    const checkActiveScenario = async () => {
      try {
        const data = await api.getCurrentScenario();
        if (data) {
          const activeScenario = data.scenario;
          setCurrentScenario(activeScenario);
          setCurrentView('attack-control');
          const startedFlag = readAttackStartFlag(
            activeScenario.id,
            activeScenario.tenant_id ?? selectedTenant?.tenant_id
          );
          const isAwaiting = !startedFlag;
          updateAwaitingUserStart(isAwaiting);
          if (isAwaiting) {
            setAttackStatus('stopped');
          }
          // Load attack configuration from S3 (use embedded s3_config from single call)
          await loadAttackConfig(activeScenario, { skipStatusUpdate: isAwaiting, s3_config: data.s3_config });
        } else {
          setCurrentView('scenario-selection');
          updateAwaitingUserStart(false);
        }
      } catch (error) {
        console.error('Error checking active scenario:', error);
        setCurrentView('scenario-selection');
      } finally {
        setInitialLoading(false);
      }
    };

    checkActiveScenario();
  }, [loadAttackConfig, readAttackStartFlag, selectedTenant?.tenant_id, updateAwaitingUserStart]);

  useEffect(() => {
    const handleScenarioChanged = (event: Event) => {
      const { detail } = event as CustomEvent<Scenario | null>;
      if (!detail) {
        if (currentScenario) {
          writeAttackStartFlag(
            currentScenario.id,
            currentScenario.tenant_id ?? selectedTenant?.tenant_id,
            false
          );
        }
        setCurrentScenario(null);
        setCurrentView('scenario-selection');
        setAttackStatus('stopped');
        updateAwaitingUserStart(false);
        return;
      }
      setCurrentScenario(detail);
      setCurrentView('attack-control');
      setAttackStatus('stopped');
      writeAttackStartFlag(detail.id, detail.tenant_id ?? selectedTenant?.tenant_id, false);
      updateAwaitingUserStart(true);
      loadAttackConfig(detail, { skipStatusUpdate: true });
    };

    window.addEventListener(SCENARIO_CHANGED_EVENT, handleScenarioChanged);
    return () => {
      window.removeEventListener(SCENARIO_CHANGED_EVENT, handleScenarioChanged);
    };
  }, [currentScenario, loadAttackConfig, selectedTenant?.tenant_id, updateAwaitingUserStart, writeAttackStartFlag]);

  // Real-time bot status stream (totals only)
  useEffect(() => {
    if (!useSSE || currentView !== 'attack-control' || !selectedTenant?.tenant_id) {
      return;
    }

    const cleanup = api.subscribeBotStatus(
      (data) => {
        applyLiveBotStatus(
          data?.pending || 0,
          data?.running || 0,
          data?.shutting_down || 0
        );
      },
      (error) => {
        console.error('Bot status stream error:', error);
      }
    );

    return () => {
      cleanup?.();
    };
  }, [applyLiveBotStatus, currentView, selectedTenant?.tenant_id, useSSE]);

  // Operations events (e.g. creation_failed): show notification and sync control panel count
  useEffect(() => {
    if (currentView !== 'attack-control' || !selectedTenant?.tenant_id) {
      return;
    }
    const cleanup = api.streamOperationsEvents(
      (evt: { type?: string; message?: string; region_name?: string; region_id?: string; provider_type?: string }) => {
        try {
          if (!evt || typeof evt !== 'object' || evt.type !== 'creation_failed') return;
          const message =
            evt.message ||
            `There was a problem creating bots in region ${formatRegionLocation(evt.region_name || evt.region_id || 'unknown')} on ${evt.provider_type || 'provider'}. Try creating bots in other regions.`;
          showNotification(message, 'warning');
          setPendingBotTarget(null);
          refreshBotInfo();
        } catch {
          // ignore
        }
      },
      (err) => {
        console.debug('Operations events stream error', err);
      }
    );
    return () => {
      try {
        cleanup?.();
      } catch {
        // ignore
      }
    };
  }, [currentView, selectedTenant?.tenant_id, showNotification, refreshBotInfo]);

  // Refresh bot status while on attack control view
  // Don't refresh when control panel has unsaved changes
  useEffect(() => {
    if (currentView !== 'attack-control' || controlPanelDirty) return;
    const intervalMs = attackStatus === 'stopped' ? 15000 : 5000;
    refreshBotInfo();
    const id = window.setInterval(refreshBotInfo, intervalMs);
    return () => window.clearInterval(id);
  }, [currentView, attackStatus, controlPanelDirty, refreshBotInfo]);

  // When dirty state becomes false (after Save/Cancel), refresh bot info immediately
  const prevDirtyRef = useRef(controlPanelDirty);
  useEffect(() => {
    if (prevDirtyRef.current && !controlPanelDirty && currentView === 'attack-control' && attackStatus !== 'stopped') {
      refreshBotInfo();
    }
    prevDirtyRef.current = controlPanelDirty;
  }, [controlPanelDirty, currentView, attackStatus, refreshBotInfo]);

  const loadScenarios = useCallback(async () => {
    // Prevent duplicate simultaneous API calls
    if (loadingScenariosRef.current) {
      console.log('Scenarios already loading, skipping duplicate call');
      return;
    }

    try {
      loadingScenariosRef.current = true;
      setScenariosLoading(true);
      setScenariosError(null);
      const data = await api.getScenarios();
      setScenarios(data);
    } catch (error) {
      console.error('Error loading scenarios:', error);
      setScenariosError('Failed to load scenarios');
      showNotification('Failed to load scenarios', 'error');
    } finally {
      setScenariosLoading(false);
      loadingScenariosRef.current = false;
    }
  }, [showNotification]);

  const loadRegions = useCallback(async () => {
    try {
      const data = await api.getRegions();
      setRegions(data);
    } catch (error) {
      console.error('Error loading regions:', error);
      showNotification('Failed to load regions', 'error');
    }
  }, [showNotification]);

  // Region capacity (warm-pool reservations for azure/gcp, quota limits elsewhere) now
  // arrives on the /regions payload as `capacity_total`, which the 10s regions refresh
  // already keeps current. The previous per-provider polling fan-out hit global-admin-only
  // /providers endpoints, so non-super-admins always saw an empty denominator.

  // Load scenarios when in selection view
  useEffect(() => {
    if (currentView === 'scenario-selection') {
      loadScenarios();
    }
  }, [currentView, selectedTenant?.tenant_id, loadScenarios]);

  // Validate scenario targets against allow list when scenarios or tenant change
  useEffect(() => {
    if (!scenarios.length || !selectedTenant?.tenant_id) {
      setScenarioAllowListOk({});
      setScenarioInvalidTargets({});
      return;
    }
    const allTargets = Array.from(
      new Set(
        scenarios.flatMap((s) => {
          const t = (s.configuration as any)?.targets;
          return Array.isArray(t) ? (t as string[]).filter(Boolean) : [];
        })
      )
    );
    if (allTargets.length === 0) {
      const ok: Record<string, boolean> = {};
      const invalid: Record<string, string[]> = {};
      scenarios.forEach((s) => {
        ok[s.id] = true;
        invalid[s.id] = [];
      });
      setScenarioAllowListOk(ok);
      setScenarioInvalidTargets(invalid);
      return;
    }
    let cancelled = false;
    api
      .validateTargetsInAllowList(selectedTenant.tenant_id, allTargets)
      .then((res) => {
        if (cancelled) return;
        const approvedByTarget = new Map<string, boolean>();
        res.validation_results.forEach((r: { target: string; is_approved: boolean }) => {
          approvedByTarget.set(r.target, r.is_approved);
        });
        const ok: Record<string, boolean> = {};
        const invalid: Record<string, string[]> = {};
        scenarios.forEach((s) => {
          const targets = Array.isArray((s.configuration as any)?.targets)
            ? ((s.configuration as any).targets as string[]).filter(Boolean)
            : [];
          const notApproved = targets.filter((t) => approvedByTarget.get(t) !== true);
          invalid[s.id] = notApproved;
          ok[s.id] = targets.length === 0 || notApproved.length === 0;
        });
        setScenarioAllowListOk(ok);
        setScenarioInvalidTargets(invalid);
      })
      .catch(() => {
        if (!cancelled) {
          setScenarioAllowListOk({});
          setScenarioInvalidTargets({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [scenarios, selectedTenant?.tenant_id]);

  // Load regions when in attack control view
  useEffect(() => {
    if (currentView === 'attack-control') {
      loadRegions();
    }
  }, [currentView, selectedTenant?.tenant_id, loadRegions]);

  // Refresh regions periodically while on attack control view
  useEffect(() => {
    if (currentView !== 'attack-control') return;
    
    // Refresh regions every 10 seconds
    const intervalId = setInterval(() => {
      loadRegions();
    }, 10000);
    
    return () => clearInterval(intervalId);
  }, [currentView, loadRegions]);

  const handleScenarioSelect = async (scenario: Scenario) => {
    try {
      setSettingScenario(true);
      showNotification(`Setting scenario: ${scenario.name}`, 'info');
      await api.setAttackScenario(scenario.id);
      // Single call: get current scenario + S3 config (avoids duplicate current-scenario + s3-config requests)
      const data = await api.getCurrentScenario();
      const activeScenario = data?.scenario ?? scenario;
      setCurrentScenario(activeScenario);
      setCurrentView('attack-control');
      setAttackStatus('stopped');
      writeAttackStartFlag(activeScenario.id, activeScenario.tenant_id ?? selectedTenant?.tenant_id, false);
      updateAwaitingUserStart(true);
      window.dispatchEvent(new CustomEvent(SCENARIO_CHANGED_EVENT, { detail: activeScenario }));
      setBotCount(10);
      await loadAttackConfig(activeScenario, { skipStatusUpdate: true, s3_config: data?.s3_config });
      showNotification(`Scenario ${scenario.name} is now active`, 'success');
    } catch (error: any) {
      console.error('Error setting scenario:', error);
      handleScenarioError(error);
    } finally {
      setSettingScenario(false);
    }
  };

  const handleScenarioError = (error: any) => {
    const errorMessage = toErrorText(error.response?.data?.detail ?? error.message ?? 'Failed to set scenario');
    showNotification(errorMessage, 'error');
    
    // Parse error details if available
    const detailMsg = toErrorText(error.response?.data?.detail || '');
    const details: string[] = [];
    
    if (detailMsg.includes('allow list')) {
      details.push('Some targets are not in the allow list');
    }
    if (detailMsg.includes('configuration')) {
      details.push('Invalid configuration');
    }
    if (detailMsg.includes('required')) {
      details.push('Missing required fields');
    }
    
    setErrorDialog({
      open: true,
      title: 'Cannot Set Scenario',
      message: detailMsg,
      details: details.length > 0 ? details : ['Check the browser console for more details']
    });
  };

  const handleCreateNew = () => {
    setEditScenario(null);
    setCreateDialogOpen(true);
  };

  const clearAssetPrefillQuery = useCallback(() => {
    if (!searchParams.has('asset')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('asset');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleCreateDialogClose = useCallback(() => {
    setCreateDialogOpen(false);
    clearAssetPrefillQuery();
  }, [clearAssetPrefillQuery]);

  const handleEditScenario = (scenario: Scenario) => {
    setEditScenario(scenario);
    setCreateDialogOpen(true);
  };

  const handleDeleteScenario = (scenario: Scenario) => {
    setDeleteConfirmScenario(scenario);
  };

  const handleRunDebugScenario = (scenario: Scenario) => {
    setDebugScenario(scenario);
  };

  const handleDuplicateScenario = async (scenario: Scenario) => {
    try {
      const duplicateName = generateDuplicateScenarioName(
        scenario.name,
        scenarios.map((item) => item.name)
      );
      await api.createScenario({
        name: duplicateName,
        description: scenario.description?.trim() || undefined,
        attack_type_id: scenario.attack_type_id,
        configuration: cloneScenarioConfiguration(scenario.configuration),
      });
      showNotification(`Scenario "${duplicateName}" duplicated`, 'success');
      await loadScenarios();
    } catch (err: any) {
      const message = err?.response?.data?.detail ?? err?.message ?? 'Failed to duplicate scenario';
      showNotification(String(message), 'error');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmScenario) return;
    setDeletingScenario(true);
    try {
      await api.deleteScenario(deleteConfirmScenario.id);
      showNotification(`Scenario "${deleteConfirmScenario.name}" deleted`, 'success');
      setDeleteConfirmScenario(null);
      loadScenarios();
    } catch (err: any) {
      showNotification(`Failed to delete scenario: ${err?.message || 'Unknown error'}`, 'error');
    } finally {
      setDeletingScenario(false);
    }
  };

  const handleCreateSuccess = () => {
    handleCreateDialogClose();
    loadScenarios(); // Reload scenarios list
    showNotification('Scenario created successfully', 'success');
  };

  const prefillAssetTarget = String(searchParams.get('asset') || '').trim();

  useEffect(() => {
    if (!prefillAssetTarget) return;
    if (handledAssetPrefillRef.current === prefillAssetTarget) return;
    handledAssetPrefillRef.current = prefillAssetTarget;
    setEditScenario(null);
    setCreateDialogOpen(true);
  }, [prefillAssetTarget]);

  const handleStart = async () => {
    try {
      if (!currentScenario) {
        showNotification('No active scenario', 'error');
        return;
      }
      setSettingScenario(true);
      showNotification('Setting scenario...', 'info');
      // Set uploads attack_config.json to S3 and sets command=run
      await api.setAttackScenario(currentScenario.id);
      setAttackStatus('running');
      writeAttackStartFlag(currentScenario.id, currentScenario.tenant_id ?? selectedTenant?.tenant_id, true);
      updateAwaitingUserStart(false);
      await loadRegions();  // Refresh regions to show updated bot counts
      showNotification('Scenario set (running)', 'success');
    } catch (error) {
      console.error('Error starting attack:', error);
      showNotification('Failed to set scenario', 'error');
    } finally {
      setSettingScenario(false);
    }
  };

  /** Combined start: set scenario (S3) then apply counts (scale). Only set running when both succeed. */
  const handleStartAttack = async (botCount: number, threadCount: number) => {
    if (!currentScenario) {
      showNotification('No active scenario', 'error');
      return;
    }
    isStartAttackInProgressRef.current = true;
    setSettingScenario(true);
    try {
      showNotification('Setting scenario...', 'info');
      await api.setAttackScenario(currentScenario.id);
      await loadRegions();
      await handleApplyCounts(botCount, threadCount);
      // Only after both steps succeed: update flags and status so UI shows Pause/End (and so config refresh doesn't flip status)
      writeAttackStartFlag(currentScenario.id, currentScenario.tenant_id ?? selectedTenant?.tenant_id, true);
      updateAwaitingUserStart(false);
      setAttackStatus('running');
      showNotification('Attack started', 'success');
      await loadRegions();
    } catch (error: any) {
      console.error('Error starting attack:', error);
      const errorMessage = toErrorText(error.response?.data?.detail ?? error.message ?? 'Failed to start attack');
      showNotification(errorMessage, 'error');
      setErrorDialog({
        open: true,
        title: 'Failed to Start Attack',
        message: errorMessage,
        details: ['Check provider/region availability', 'Verify permissions', 'Try again in a few seconds'],
      });
      setAttackStatus('stopped');
      updateAwaitingUserStart(true);
    } finally {
      setSettingScenario(false);
      isStartAttackInProgressRef.current = false;
    }
  };

  const handlePause = async () => {
    try {
      showNotification('Pausing attack...', 'info');
      await api.pauseAttack();
      setAttackStatus('paused');
      if (currentScenario) {
        writeAttackStartFlag(currentScenario.id, currentScenario.tenant_id ?? selectedTenant?.tenant_id, true);
      }
      updateAwaitingUserStart(false);
      showNotification('Attack paused', 'success');
    } catch (error) {
      console.error('Error pausing attack:', error);
      showNotification('Failed to pause attack', 'error');
    }
  };

  const handleEnd = async () => {
    try {
      showNotification('Ending attack...', 'info');
      // Treat end as shutdown command (scenario remains set until End Scenario)
      await api.setAttackCommand('shutdown');
      setAttackStatus('stopped');
      if (currentScenario) {
        writeAttackStartFlag(currentScenario.id, currentScenario.tenant_id ?? selectedTenant?.tenant_id, false);
      }
      updateAwaitingUserStart(false);
      showNotification('Attack ended', 'success');
    } catch (error) {
      console.error('Error ending attack:', error);
      showNotification('Failed to end attack', 'error');
    }
  };

  const getStoredScopedRegionIds = useCallback((sourceRegions: Region[]): string[] => {
    const STORAGE_KEY = 'operations.regionsFiltersState';
    const parseFilterQuery = (queryStr: string) => {
      const trimmed = queryStr.trim().toLowerCase();
      if (!trimmed) return null;
      const filters: {
        continent?: string;
        providerType?: string;
        providerName?: string;
        regionName?: string;
        regionCode?: string;
        location?: string;
        status?: string;
        configured?: string;
        bots?: string;
        capacity?: string;
        freeText?: string;
        negations: Set<string>;
      } = { negations: new Set() };

      const filterPattern = /(!?)(\w+)(?:=|:)\s*"([^"]*)"|(!?)(\w+)(?:=|:)\s*([^\s]+)/gi;
      let match: RegExpExecArray | null;
      let lastIndex = 0;
      const freeTextParts: string[] = [];

      while ((match = filterPattern.exec(trimmed)) !== null) {
        if (match.index > lastIndex) {
          freeTextParts.push(trimmed.substring(lastIndex, match.index).trim());
        }
        lastIndex = match.index + match[0].length;

        const negation = match[1] || match[4];
        const key = (match[2] || match[5]).toLowerCase();
        let value = (match[3] !== undefined ? match[3] : match[6]).toLowerCase();
        value = value.replace(/\*/g, '');

        if (key === 'continent') {
          filters.continent = value;
          if (negation) filters.negations.add('continent');
        } else if (key === 'provider_type' || key === 'type') {
          filters.providerType = value;
          if (negation) filters.negations.add('providerType');
        } else if (key === 'provider' || key === 'provider_name') {
          filters.providerName = value;
          if (negation) filters.negations.add('providerName');
        } else if (key === 'region' || key === 'region_name' || key === 'name') {
          filters.regionName = value;
          if (negation) filters.negations.add('regionName');
        } else if (key === 'region_code' || key === 'code') {
          filters.regionCode = value;
          if (negation) filters.negations.add('regionCode');
        } else if (key === 'location') {
          filters.location = value;
          if (negation) filters.negations.add('location');
        } else if (key === 'status') {
          filters.status = value;
          if (negation) filters.negations.add('status');
        } else if (key === 'configured') {
          filters.configured = value;
          if (negation) filters.negations.add('configured');
        } else if (key === 'bots' || key === 'machine_count') {
          filters.bots = value;
          if (negation) filters.negations.add('bots');
        } else if (key === 'capacity') {
          filters.capacity = value;
          if (negation) filters.negations.add('capacity');
        }
      }

      if (lastIndex < trimmed.length) {
        freeTextParts.push(trimmed.substring(lastIndex).trim());
      }
      const freeText = freeTextParts.filter(Boolean).join(' ').trim();
      if (freeText) {
        filters.freeText = freeText;
      }
      return filters;
    };

    const matchesNumericFilter = (rawFilter: string | undefined, actualValue: number): boolean => {
      if (!rawFilter) return true;
      const normalized = rawFilter.replace(/\s/g, '');
      const match = normalized.match(/^(>=|<=|>|<|=)?(-?\d+(?:\.\d+)?)$/);
      if (!match) return false;
      const operator = match[1] || '=';
      const target = Number(match[2]);
      switch (operator) {
        case '>':
          return actualValue > target;
        case '>=':
          return actualValue >= target;
        case '<':
          return actualValue < target;
        case '<=':
          return actualValue <= target;
        default:
          return actualValue === target;
      }
    };

    const matchesStringFilter = (value: string, rawFilter: string, isNegated: boolean) => {
      value = normalizeLocationSearch(value);
      rawFilter = normalizeLocationSearch(rawFilter);
      const terms = rawFilter
        .split(/[,&|]/)
        .map((term) => term.trim())
        .filter(Boolean);
      if (terms.length === 0) return true;
      const matchesAny = terms.some((term) => value.includes(term));
      return isNegated ? !matchesAny : matchesAny;
    };

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as {
        activeFilters?: Array<{ query?: string }>;
        runningOnly?: boolean;
        providerTypeFilters?: string[];
      };

      const activeQueries = (parsed.activeFilters || [])
        .map((filter) => filter?.query || '')
        .filter(Boolean);
      const parsedQueries = activeQueries
        .map((query) => parseFilterQuery(query))
        .filter((entry): entry is NonNullable<ReturnType<typeof parseFilterQuery>> => Boolean(entry));

      return sourceRegions
        .filter((region) => {
          if ((parsed.runningOnly ?? false) && (region.machine_count || 0) <= 0) return false;

          const providerTypeFilters = Array.isArray(parsed.providerTypeFilters)
            ? parsed.providerTypeFilters.map((value) => value.toLowerCase())
            : [];
          if (providerTypeFilters.length > 0) {
            const providerType = region.provider?.provider_type?.toLowerCase();
            if (!providerType || !providerTypeFilters.includes(providerType)) return false;
          }

          if (parsedQueries.length === 0) return true;

          return parsedQueries.some((filters) => {
            const provider = region.provider;
            if (filters.continent) {
              const isNegated = filters.negations.has('continent');
              if (!matchesStringFilter(getRegionLocation(region).continent.toLowerCase(), filters.continent, isNegated)) return false;
            }
            if (filters.providerType) {
              if (!provider) return false;
              const isNegated = filters.negations.has('providerType');
              if (!matchesStringFilter((provider.provider_type || '').toLowerCase(), filters.providerType, isNegated)) return false;
            }
            if (filters.providerName) {
              if (!provider) return false;
              const isNegated = filters.negations.has('providerName');
              if (!matchesStringFilter((provider.name || '').toLowerCase(), filters.providerName, isNegated)) return false;
            }
            if (filters.regionName) {
              const isNegated = filters.negations.has('regionName');
              if (!matchesStringFilter(regionSearchText(region), filters.regionName, isNegated)) return false;
            }
            if (filters.regionCode) {
              const isNegated = filters.negations.has('regionCode');
              if (!matchesStringFilter((region.region_code || '').toLowerCase(), filters.regionCode, isNegated)) return false;
            }
            if (filters.location) {
              const isNegated = filters.negations.has('location');
              if (!matchesStringFilter(normalizeLocationSearch(getRegionLocation(region).label), filters.location, isNegated)) return false;
            }
            if (filters.status) {
              const isNegated = filters.negations.has('status');
              if (!matchesStringFilter((region.status || '').toLowerCase(), filters.status, isNegated)) return false;
            }
            if (filters.configured) {
              const configuredValue = ['true', '1', 'yes', 'y'].includes(filters.configured);
              const matches = region.configured === configuredValue;
              const isNegated = filters.negations.has('configured');
              if (isNegated ? matches : !matches) return false;
            }
            if (filters.bots) {
              const matches = matchesNumericFilter(filters.bots, region.machine_count || 0);
              const isNegated = filters.negations.has('bots');
              if (isNegated ? matches : !matches) return false;
            }
            if (filters.capacity) {
              const capacityValue = (region.capacity ?? (region as any).capacity ?? 0) as number;
              const matches = matchesNumericFilter(filters.capacity, capacityValue);
              const isNegated = filters.negations.has('capacity');
              if (isNegated ? matches : !matches) return false;
            }
            if (filters.freeText) {
              const searchText = `${regionSearchText(region)} ${provider?.name || ''} ${provider?.provider_type || ''}`.toLowerCase();
              return matchesStringFilter(searchText, filters.freeText, false);
            }
            return true;
          });
        })
        .map((region) => region.id)
        .filter((id): id is string => Boolean(id));
    } catch {
      return [];
    }
  }, []);

  const handleApplyCounts = async (newBotCount: number, newThreadCount: number) => {
    try {
      setSettingCounts(true);
      showNotification('Saving configuration and scaling bots...', 'info');

      const targetBots = Math.max(0, newBotCount);
      const currentTotal = botStatus.running + botStatus.pending;
      if (targetBots !== currentTotal) {
        setPendingBotTarget({
          target: targetBots,
          direction: targetBots > currentTotal ? 'up' : 'down',
          startedAt: Date.now(),
        });
      }
      setBotCount(targetBots);

      // 1. Fetch latest regions for capacity check (Parallelized with S3 update removal)
      // Note: We skip explicit S3 update here because createBots endpoint handles it
      const latestRegions = await api.getRegions();

      // 2. Scale bots to match desired count IN SELECTED REGIONS
      // IMPORTANT: Preserve existing region selection logic
      // selectedRegions comes from SmartFilterBar filtering in RegionsPanel
      const storedScopedRegionIds = selectedRegions.length > 0
        ? []
        : getStoredScopedRegionIds(latestRegions);
      const regionIds = selectedRegions.length > 0
        ? selectedRegions
        : storedScopedRegionIds.length > 0
          ? storedScopedRegionIds
          : latestRegions.filter((r) => r.configured && r.id).map((r) => r.id as string);

      const regionSelections: CreateBotRegionSelection[] = regionIds.map(regionId => {
        const region = latestRegions.find(r => r.id === regionId);
        return {
          region_id: regionId,
          provider_id: region?.provider?.id,
          region_name: region?.name
        };
      });

      if (regionSelections.length === 0) {
        throw new Error('No regions available. Please configure at least one region.');
      }

      // 2.1 Pre-check effective instance limits (best-effort UX guardrail)
      const selectedRegionData = regionSelections
        .map((selection) => latestRegions.find((region) => region.id === selection.region_id))
        .filter(Boolean) as Region[];

      // Bots already running in regions that are NOT in the current scope (e.g. a previously
      // selected region that the user has since removed from the filter). The backend only
      // counts bots inside the requested regions when computing the delta, so we must subtract
      // the out-of-scope bots ourselves to avoid creating duplicates.
      const totalActiveInScope = selectedRegionData.reduce((sum, region) => sum + (region.machine_count || 0), 0);
      const botsOutsideScope = Math.max(0, currentTotal - totalActiveInScope);
      const adjustedAmount = Math.max(0, targetBots - botsOutsideScope);
      const botDifference = adjustedAmount - totalActiveInScope;

      // Number of bots we actually request the backend to create in scope. The
      // distribution must fit each region's available capacity, so this may be
      // capped below `adjustedAmount` when the selected regions can't hold all
      // the requested bots.
      let inScopeTarget = adjustedAmount;

      if (botDifference > 0 && selectedRegionData.length > 0) {
        // For azure/gcp the number of reserved units is a hard cap: a region can
        // hold at most that many bots. Resolve the reserved total the same way the
        // Regions panel does (provider_id:{region_code|id|name}). Only trusted when
        // region capacity data is actually loaded, so stale/empty state doesn't wrongly
        // zero out capacity (the backend re-checks reservations authoritatively).
        const hasReservationData = Object.keys(regionCapacityCounts).length > 0;
        const resolveReservedTotal = (region: Region): number | null => {
          const providerType = (region.provider?.provider_type ?? '').toLowerCase();
          if (providerType !== 'azure' && providerType !== 'gcp') return null;
          if (!hasReservationData) return null;
          const providerId = (region.provider?.id ?? '').toLowerCase();
          const candidates = [region.region_code, region.id, region.name]
            .filter((value): value is string => Boolean(value))
            .map((value) => `${providerId}:${value.toLowerCase()}`);
          for (const candidate of candidates) {
            if (regionCapacityCounts[candidate] !== undefined) {
              return Math.max(0, Math.round(regionCapacityCounts[candidate]));
            }
          }
          return 0; // azure/gcp region with no reservations => no reserved spots
        };

        const working = selectedRegionData.map((region) => {
          const reservedTotal = resolveReservedTotal(region);
          // The tightest of the quota limit and reserved units bounds the region.
          const limitCandidates: number[] = [];
          if (region.effective_instance_limit !== null && region.effective_instance_limit !== undefined) {
            limitCandidates.push(region.effective_instance_limit);
          }
          if (reservedTotal !== null) {
            limitCandidates.push(reservedTotal);
          }
          return {
            id: region.id as string,
            name: region.name,
            count: region.machine_count || 0,
            limit: limitCandidates.length > 0 ? Math.min(...limitCandidates) : null
          };
        });

        const allLimitsUnconfigured = working.every(
          (region) => region.limit === null || region.limit === undefined
        );
        if (allLimitsUnconfigured) {
          const message =
            'No instance limits are configured for the selected regions. ' +
            'Set a manual bot limit in Region Management or run a capacity sync before creating bots.';
          showNotification(message, 'error');
          throw new Error(message);
        }

        // Greedily assign each new bot to the region with the fewest bots that
        // still has available capacity (count < limit; a null/undefined limit is
        // treated as unlimited). Regions already at capacity are skipped, so a
        // region with N free slots receives at most N new bots and nothing
        // overflows its effective instance limit.
        const hasCapacity = (region: typeof working[number]) =>
          region.limit === null || region.limit === undefined || region.count < (region.limit as number);

        let placed = 0;
        for (let i = 0; i < botDifference; i += 1) {
          const candidates = working.filter(hasCapacity);
          if (candidates.length === 0) break; // every selected region is full
          candidates.sort((a, b) => a.count - b.count);
          const target = candidates[0];
          target.count += 1;
          placed += 1;
        }

        inScopeTarget = totalActiveInScope + placed;

        if (placed < botDifference) {
          const cappedTotal = botsOutsideScope + inScopeTarget;
          showNotification(
            `Only ${placed} of ${botDifference} requested bots fit the available region capacity. Scaling to ${cappedTotal}.`,
            'warning'
          );
          // Reflect the capacity-capped target so the UI doesn't keep waiting on
          // bots that can't be created.
          setBotCount(cappedTotal);
          setPendingBotTarget((prev) =>
            prev ? { ...prev, target: cappedTotal, direction: cappedTotal > currentTotal ? 'up' : 'down' } : prev
          );
        }
      }

      await api.createBots(inScopeTarget, regionSelections, newThreadCount, undefined, currentScenario?.id);

      // 3. Refresh bot status and regions (Parallel)
      setThreadCount(newThreadCount);
      // `threads` is part of the attack configuration, so an applied thread count has to land in
      // the config the JSON view renders and the next config write sends — otherwise the stale
      // value reappears and overwrites what was just scaled (CAO-237). An empty config is one
      // that has not loaded (or failed to), not one with no fields: seeding it with a lone
      // `threads` would leave the JSON view showing a configuration the scenario does not have.
      setAttackConfig((prev) => {
        if (Object.keys(prev).length === 0 || prev.threads === newThreadCount) return prev;
        return { ...prev, threads: newThreadCount };
      });
      await Promise.all([
        refreshBotInfo(),
        loadRegions()
      ]);
      
      const scaledTotal = botsOutsideScope + inScopeTarget;
      showNotification(`Configuration saved. Scaled to ${scaledTotal} bots with ${newThreadCount} threads.`, 'success');
    } catch (error: any) {
      console.error('Error applying counts:', error);
      setPendingBotTarget(null);
      await refreshBotInfo();
      const errorMessage = toErrorText(error.response?.data?.detail ?? error.message ?? 'Failed to apply bot/thread settings');
      showNotification(errorMessage, 'error');
      setErrorDialog({
        open: true,
        title: 'Failed to Apply Settings',
        message: errorMessage,
        details: ['Check provider/region availability', 'Verify permissions', 'Try again in a few seconds']
      });
    } finally {
      setSettingCounts(false);
    }
  };

  const performEndScenario = async (impactBots: number) => {
    try {
      setEndingScenario(true);
      showNotification('Ending scenario...', 'info');
      const result = await api.endScenario(impactBots);
      console.log('End scenario result:', result);
      if (currentScenario) {
        writeAttackStartFlag(currentScenario.id, currentScenario.tenant_id ?? selectedTenant?.tenant_id, false);
      }
      setCurrentScenario(null);
      setCurrentView('scenario-selection');
      setAttackStatus('stopped');
      updateAwaitingUserStart(false);
      window.dispatchEvent(new CustomEvent(SCENARIO_CHANGED_EVENT, { detail: null }));
      await loadRegions();  // Refresh regions to show terminated bots
      showNotification('Scenario ended successfully', 'success');
    } catch (error: any) {
      console.error('Error ending scenario:', error);
      const errorMessage = toErrorText(error.response?.data?.detail ?? error.message ?? 'Failed to end scenario');
      showNotification(errorMessage, 'error');

      // Show error dialog with details
      setErrorDialog({
        open: true,
        title: 'Failed to End Scenario',
        message: errorMessage,
        details: ['Check if you have the required permissions', 'Verify the scenario is currently active']
      });
    } finally {
      setEndingScenario(false);
    }
  };

  // Opens the impact-bots prompt; the scenario is only ended once the user confirms it.
  const handleEndScenario = () => {
    setEndScenarioDialogOpen(true);
  };

  const handleCancelEndScenarioDialog = () => {
    setEndScenarioDialogOpen(false);
  };

  const handleConfirmEndScenario = (impactBots: number) => {
    setEndScenarioDialogOpen(false);
    performEndScenario(impactBots);
  };

  const getRunningCountForRegions = useCallback(
    (regionIds: string[], regionsList: Region[]) => {
      const idSet = new Set(regionIds.map((id) => id.toLowerCase()));
      const pid = (p: string | undefined) => (p ?? '').toLowerCase();
      return regionsList.reduce((sum, region) => {
        if (!idSet.has((region.id ?? '').toLowerCase())) return sum;
        const providerId = pid(region.provider?.id);
        const v = [region.id, region.region_code, region.name]
          .filter((x): x is string => Boolean(x))
          .map((x) => regionRunningCounts[`${providerId}:${x.toLowerCase()}`])
          .find((x) => x !== undefined);
        return sum + (v ?? 0);
      }, 0);
    },
    [regionRunningCounts]
  );

  const handleShutdownRegions = async (regionIds: string[]) => {
    if (regionIds.length === 0) {
      showNotification('No regions selected', 'warning');
      return;
    }
    try {
      setRegionActionLoading(true);
      showNotification('Shutting down bots in selected regions...', 'info');
      const currentTotal = botStatus.running + botStatus.pending;
      const runningInRegions = getRunningCountForRegions(regionIds, regions);
      const targetBots = Math.max(0, currentTotal - runningInRegions);
      if (targetBots !== currentTotal) {
        setPendingBotTarget({
          target: targetBots,
          direction: 'down',
          startedAt: Date.now(),
        });
        setBotCount(targetBots);
      }
      await api.shutdownBots(regionIds);
      await refreshBotInfo();
      await loadRegions();
      showNotification('Shutdown initiated for selected regions', 'success');
    } catch (error: any) {
      console.error('Error shutting down bots in regions:', error);
      setPendingBotTarget(null);
      await refreshBotInfo();
      const errorMessage = toErrorText(error.response?.data?.detail ?? error.message ?? 'Failed to shutdown bots');
      showNotification(errorMessage, 'error');
    } finally {
      setRegionActionLoading(false);
    }
  };

  const handleTerminateRegions = async (regionIds: string[]) => {
    if (regionIds.length === 0) {
      showNotification('No regions selected', 'warning');
      return;
    }
    try {
      setRegionActionLoading(true);
      showNotification('Terminating bots in selected regions...', 'info');
      const currentTotal = botStatus.running + botStatus.pending;
      const runningInRegions = getRunningCountForRegions(regionIds, regions);
      const targetBots = Math.max(0, currentTotal - runningInRegions);
      if (targetBots !== currentTotal) {
        setPendingBotTarget({
          target: targetBots,
          direction: 'down',
          startedAt: Date.now(),
        });
        setBotCount(targetBots);
      }
      await api.terminateBots(regionIds);
      await refreshBotInfo();
      await loadRegions();
      showNotification('Termination initiated for selected regions', 'success');
    } catch (error: any) {
      console.error('Error terminating bots in regions:', error);
      setPendingBotTarget(null);
      await refreshBotInfo();
      const errorMessage = toErrorText(error.response?.data?.detail ?? error.message ?? 'Failed to terminate bots');
      showNotification(errorMessage, 'error');
    } finally {
      setRegionActionLoading(false);
    }
  };

  const handleConfigUpdate = useCallback(async (config: Record<string, any>) => {
    try {
      setAttackConfig(config);
      
      // Update thread count if it changed in config
      if (config.threads && config.threads !== threadCount) {
        setThreadCount(config.threads);
      }
    } catch (error) {
      console.error('Error updating configuration:', error);
      showNotification('Failed to update configuration', 'error');
    }
  }, [threadCount, showNotification]);

  const handleApplyConfig = useCallback(async (config: Record<string, any>) => {
    if (!currentScenario) {
      showNotification('No active scenario', 'error');
      return;
    }

    try {
      // The backend rebuilds attack_config.json from this body, reading a missing `threads` as
      // the default of 1. Always carry the thread count the operator is looking at so a config
      // write can never silently reset it (CAO-237).
      const configThreads = Number(config.threads);
      const payload = Number.isFinite(configThreads) && configThreads >= 1
        ? config
        : { ...config, threads: threadCount };
      await api.updateScenarioConfig(currentScenario.id, payload);
      // Reload canonical config from current-scenario (single call includes S3 config).
      const data = await api.getCurrentScenario();
      const refreshed = data?.s3_config;
      if (refreshed?.exists && refreshed.s3_config) {
        const fullConfig = refreshed.s3_config.full_config || refreshed.s3_config;
        const normalizedConfig = normalizeConfigForUi(fullConfig, currentScenario.attack_type?.id);
        if (Object.keys(normalizedConfig).length > 0) {
          setAttackConfig(normalizedConfig);
        }
        const refreshedThreads =
          typeof normalizedConfig.threads === 'number'
            ? normalizedConfig.threads
            : refreshed.s3_config.threads;
        if (typeof refreshedThreads === 'number') {
          setThreadCount(refreshedThreads);
        }
      }
      showNotification('Configuration updated in S3', 'success');
    } catch (error: any) {
      console.error('Error updating configuration in S3:', error);
      const errorMessage = toErrorText(error.response?.data?.detail ?? error.message ?? 'Failed to update configuration');
      showNotification(errorMessage, 'error');
      throw error;
    }
  }, [currentScenario, normalizeConfigForUi, showNotification, threadCount, toErrorText]);

  const handleRegionSelectionChange = (regions: string[]) => {
    setSelectedRegions(regions);
  };

  if (initialLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress
          sx={{
            color: '#00f0ff',
            filter: 'drop-shadow(0 0 10px rgba(0, 240, 255, 0.6))',
          }}
        />
      </Box>
    );
  }

  return (
    <PermissionGate permission="access_operations">
      <Box>
        {currentView !== 'scenario-selection' && (
          <>
            <Box
              sx={{
                mb: 4,
                pt: 3,
                px: { xs: 2, md: 4 },
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  fontFamily: '"JetBrains Mono", monospace',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.background.default} 50%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: `0 0 30px ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.2)}`,
                  letterSpacing: '-0.02em',
                  mb: 1,
                }}
              >
                OPERATIONS
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontFamily: '"Space Mono", monospace',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                Live scenario control and command center
              </Typography>
            </Box>

            <Box
              sx={{
                mb: 3,
                px: { xs: 2, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label={`Status: ${attackStatus}`}
                  sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: '0.85rem',
                    borderWidth: '1px',
                    borderColor:
                      attackStatus === 'running'
                        ? 'rgba(0, 255, 136, 0.4)'
                        : attackStatus === 'paused'
                          ? 'rgba(255, 193, 7, 0.4)'
                          : 'rgba(255, 51, 102, 0.4)',
                    color:
                      attackStatus === 'running'
                        ? '#00ff88'
                        : attackStatus === 'paused'
                          ? '#ffc107'
                          : '#ff3366',
                    backgroundColor:
                      attackStatus === 'running'
                        ? 'rgba(0, 255, 136, 0.15)'
                        : attackStatus === 'paused'
                          ? 'rgba(255, 193, 7, 0.15)'
                          : 'rgba(255, 51, 102, 0.15)',
                  }}
                />
                <Chip
                  label={
                    currentScenario?.attack_type?.attack_class
                      ? `Attack category: ${currentScenario.attack_type.attack_class}`
                      : 'Attack category: infra / application / volumetric'
                  }
                  variant="outlined"
                  sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: '0.85rem',
                    borderWidth: '1px',
                    borderColor: currentScenario?.attack_type?.attack_class
                      ? `${getAttackClassColor(currentScenario.attack_type.attack_class)}40`
                      : 'rgba(0, 240, 255, 0.4)',
                    color: 'text.secondary',
                    backgroundColor: 'transparent',
                  }}
                />
                <Chip
                  label={currentScenario ? `Scenario: ${currentScenario.name}` : 'Scenario: None'}
                  variant="outlined"
                  sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: '0.85rem',
                    borderWidth: '1px',
                    borderColor: currentScenario ? 'rgba(0, 240, 255, 0.4)' : 'rgba(255, 0, 110, 0.3)',
                    color: 'text.secondary',
                  }}
                />
                {scenarioHosting && (
                  <Tooltip title={`Hosting: ${providerLabel(scenarioHosting)}`} enterDelay={300} leaveDelay={0}>
                    <Chip
                      label={providerLabel(scenarioHosting)}
                      color={providerChipColor(scenarioHosting)}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.85rem' }}
                    />
                  </Tooltip>
                )}
              </Box>

              {/* Botnet pill + Targets pill (same row) */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                {/* Single unified scope pill */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0,
                    height: 28,
                    borderRadius: '6px',
                    border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.28)}`,
                    overflow: 'hidden',
                  }}
                >
                  {/* Botnet label */}
                  <Typography
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      px: 1.25,
                      lineHeight: '28px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Botnet:
                  </Typography>
                  <Box
                    sx={{
                      width: '1px',
                      height: '60%',
                      backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.22),
                    }}
                  />
                  {/* Active filter segments */}
                  {[
                    ...(regionsFilterSummary.runningOnly ? ['Running'] : []),
                    ...regionsFilterSummary.providerTypeFilters.map((t) => t.toUpperCase()),
                    ...regionsFilterSummary.activeFilters.map((f) => f.label || f.query),
                  ].map((label, i, arr) => (
                    <React.Fragment key={label}>
                      <Typography
                        sx={{
                          fontFamily: '"Space Mono", monospace',
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          px: 1.25,
                          lineHeight: '28px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </Typography>
                      {i < arr.length - 1 && (
                        <Box
                          sx={{
                            width: '1px',
                            height: '60%',
                            backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.22),
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}

                  {/* Divider before count */}
                  {(regionsFilterSummary.runningOnly ||
                    regionsFilterSummary.providerTypeFilters.length > 0 ||
                    regionsFilterSummary.activeFilters.length > 0) && (
                    <Box
                      sx={{
                        width: '1px',
                        height: '60%',
                        backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.22),
                      }}
                    />
                  )}

                  {/* Scope count segment */}
                  <Typography
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.75rem',
                      color: alpha(theme.palette.primary.main, isDark ? 0.8 : 0.7),
                      fontWeight: 700,
                      px: 1.25,
                      lineHeight: '28px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {regionsFilterSummary.inScopeCount}/{regionsFilterSummary.totalRegions} regions
                  </Typography>
                </Box>

                {/* Targets pill: comma-separated list, copy, expand when > 5 */}
                {currentScenario && attackTargets.length > 0 && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      height: 28,
                      borderRadius: '6px',
                      border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.28)}`,
                      overflow: 'hidden',
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography sx={{ px: 1.25, lineHeight: '28px', whiteSpace: 'nowrap' }}>
                      Targets:
                    </Typography>
                    <Box
                      sx={{
                        width: '1px',
                        height: '60%',
                        backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.22),
                      }}
                    />
                    {(() => {
                      const MAX_INLINE = 5;
                      const hasMore = attackTargets.length > MAX_INLINE;
                      const visible = hasMore ? attackTargets.slice(0, MAX_INLINE) : attackTargets;
                      const moreCount = attackTargets.length - MAX_INLINE;
                      return (
                        <>
                          <Typography
                            component="span"
                            sx={{
                              px: 1,
                              lineHeight: '28px',
                              color: 'text.secondary',
                              maxWidth: hasMore ? 220 : undefined,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={attackTargets.join(', ')}
                          >
                            {visible.join(', ')}
                            {hasMore && ` … +${moreCount} more`}
                          </Typography>
                          {hasMore && (
                            <>
                              <Typography
                                component="button"
                                type="button"
                                onClick={(e) => setTargetsExpandAnchor(e.currentTarget)}
                                sx={{
                                  fontFamily: '"Space Mono", monospace',
                                  fontSize: '0.75rem',
                                  color: theme.palette.primary.main,
                                  cursor: 'pointer',
                                  background: 'none',
                                  border: 'none',
                                  padding: '0 8px',
                                  lineHeight: '28px',
                                  whiteSpace: 'nowrap',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                                aria-label={`Show all ${attackTargets.length} targets`}
                              >
                                Expand
                              </Typography>
                              <Popover
                                open={Boolean(targetsExpandAnchor)}
                                anchorEl={targetsExpandAnchor}
                                onClose={() => setTargetsExpandAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                slotProps={{
                                  paper: { sx: { marginTop: 1, maxHeight: 320, overflow: 'auto' } },
                                }}
                              >
                                <Box sx={{ p: 2, minWidth: 280 }}>
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    All targets
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: '"Space Mono", monospace',
                                      wordBreak: 'break-all',
                                      whiteSpace: 'pre-wrap',
                                      mb: 1.5,
                                    }}
                                  >
                                    {attackTargets.join(', ')}
                                  </Typography>
                                  <Button
                                    size="small"
                                    startIcon={<ContentCopyIcon />}
                                    onClick={() => {
                                      handleCopyTargets();
                                      setTargetsExpandAnchor(null);
                                    }}
                                    sx={{ fontFamily: '"Space Mono", monospace' }}
                                  >
                                    Copy all
                                  </Button>
                                </Box>
                              </Popover>
                            </>
                          )}
                          <Tooltip title="Copy targets">
                            <IconButton
                              size="small"
                              onClick={handleCopyTargets}
                              aria-label="Copy targets"
                              sx={{
                                color: 'text.secondary',
                                p: 0.5,
                                mr: 0.5,
                                '&:hover': { color: 'primary.main' },
                              }}
                            >
                              <ContentCopyIcon sx={{ fontSize: '0.875rem' }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}

        {/* Scenario Selection View with Container */}
        {currentView === 'scenario-selection' && (
          <Container maxWidth="xl" sx={{ mt: 4 }}>
            <ScenarioSelectionView
              scenarios={scenarios}
              loading={scenariosLoading}
              error={scenariosError}
              onScenarioSelect={handleScenarioSelect}
              onScenarioRunDebug={handleRunDebugScenario}
              onScenarioEdit={handleEditScenario}
              onScenarioDuplicate={handleDuplicateScenario}
              onScenarioDelete={handleDeleteScenario}
              onCreateNew={handleCreateNew}
              settingScenario={settingScenario}
              activeScenarioId={currentScenario?.id ?? null}
              scenarioAllowListOk={scenarioAllowListOk}
              scenarioInvalidTargets={scenarioInvalidTargets}
            />
          </Container>
        )}

        {/* Attack Control View - Full width, panels overlay */}
        {currentView === 'attack-control' && currentScenario && (
          <AttackControlView
            scenario={currentScenario}
            botCount={botCount}
            threadCount={threadCount}
            botStatus={displayBotStatus}
            attackStatus={attackStatus}
            attackConfig={attackConfig}
            regions={regions}
            selectedRegions={selectedRegions}
            regionRunningCounts={regionRunningCounts}
            regionCapacityCounts={regionCapacityCounts}
            controlRef={controlRef}
            onBotCountChange={setBotCount}
            onThreadCountChange={setThreadCount}
            // Apply counts should scale bots + persist threads
            onApplyCounts={handleApplyCounts}
            onStart={handleStart}
            onStartAttack={handleStartAttack}
            onPause={handlePause}
            onEnd={handleEnd}
            onEndScenario={handleEndScenario}
            onConfigUpdate={handleConfigUpdate}
            onApplyConfig={handleApplyConfig}
            onRegionSelectionChange={handleRegionSelectionChange}
            onDirtyChange={setControlPanelDirty}
            endingScenario={endingScenario}
            isApplyingCounts={settingCounts}
            onShutdownRegions={handleShutdownRegions}
            onTerminateRegions={handleTerminateRegions}
            isRegionActionLoading={regionActionLoading}
          />
        )}

        {/* Create Scenario Dialog */}
        <CreateScenarioDialog
          open={createDialogOpen}
          onClose={handleCreateDialogClose}
          onSuccess={handleCreateSuccess}
          mode={editScenario ? 'edit' : 'create'}
          scenarioToEdit={editScenario}
          initialTarget={editScenario ? undefined : prefillAssetTarget || undefined}
          onError={(message) => {
            setErrorDialog({
              open: true,
              title: editScenario ? 'Failed to Update Scenario' : 'Failed to Create Scenario',
              message: message,
              details: ['Check that all required fields are filled', 'Verify targets are properly formatted']
            });
          }}
        />

        {/* Run Debug Dialog — single bot + in-UI terminal */}
        <ScenarioDebugDialog
          open={Boolean(debugScenario)}
          scenario={debugScenario}
          onClose={() => setDebugScenario(null)}
        />


        {/* Delete Scenario Confirmation Dialog */}
        <Dialog
          open={!!deleteConfirmScenario}
          onClose={() => !deletingScenario && setDeleteConfirmScenario(null)}
          PaperProps={{
            sx: {
              backgroundColor: (theme) => theme.palette.background.paper,
              backdropFilter: 'blur(20px)',
              border: (theme) =>
                `2px solid ${
                  theme.palette.mode === 'dark' ? 'rgba(255, 51, 102, 0.4)' : 'rgba(220, 38, 38, 0.28)'
                }`,
              borderRadius: '12px',
              minWidth: '380px',
            },
          }}
        >
          <DialogTitle
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 700,
              color: 'error.main',
              letterSpacing: '0.05em',
            }}
          >
            DELETE SCENARIO
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: 'text.primary' }}>
              Are you sure you want to delete{' '}
              <strong style={{ fontFamily: '"JetBrains Mono", monospace' }}>
                {deleteConfirmScenario?.name}
              </strong>
              ? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setDeleteConfirmScenario(null)}
              disabled={deletingScenario}
              sx={{ fontFamily: '"Space Mono", monospace' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={deletingScenario}
              startIcon={deletingScenario ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ fontFamily: '"Space Mono", monospace', fontWeight: 600 }}
            >
              {deletingScenario ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Impact Bots Dialog — shown before an End Scenario actually ends the scenario */}
        <EndScenarioImpactBotsDialog
          open={endScenarioDialogOpen}
          defaultImpactBots={botStatus.running}
          onCancel={handleCancelEndScenarioDialog}
          onConfirm={handleConfirmEndScenario}
        />

        {/* Error Dialog */}
        <Dialog
          open={errorDialog.open}
          onClose={() => setErrorDialog(prev => ({ ...prev, open: false }))}
          PaperProps={{
            sx: {
              backgroundColor: (theme) => theme.palette.background.paper,
              backdropFilter: 'blur(20px)',
              border: (theme) =>
                `2px solid ${
                  theme.palette.mode === 'dark' ? 'rgba(255, 51, 102, 0.4)' : 'rgba(220, 38, 38, 0.28)'
                }`,
            }
          }}
        >
          <DialogTitle sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'error.main' }}>
            {errorDialog.title}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontFamily: '"Space Mono", monospace', color: 'text.primary', mb: 2 }}>
              {typeof errorDialog.message === 'string' ? errorDialog.message : JSON.stringify(errorDialog.message)}
            </DialogContentText>
            {errorDialog.details && errorDialog.details.length > 0 && (
              <Box component="ul" sx={{ color: 'text.secondary', fontFamily: '"Space Mono", monospace', pl: 3 }}>
                {errorDialog.details.map((detail, idx) => (
                  <li key={idx}>{detail}</li>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => setErrorDialog(prev => ({ ...prev, open: false }))}
              variant="contained"
              color="error"
              sx={{ fontFamily: '"Space Mono", monospace', fontWeight: 600 }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PermissionGate>
  );
};

export default Operations;

